import React, { useEffect, useRef, useState } from 'react'
import './ProductRail.css'

/**
 * Single-row product carousel (shop categories, product-page suggestions): heading with prev/next
 * arrows and a scroll-snapping row of cards, swipeable on touch. Arrows hide while there is nothing
 * to scroll. Children are the row's <li> items. `arrows="sides"` puts the arrows over the row's left and
 * right edges and drops the heading (for sections that render their own heading).
 * Optional `eyebrow` + `icon` label the heading, `actions` sit beside the arrows and `className`
 * styles one rail (e.g. the product page's framed panels).
 */
function ProductRail({ id, title, subtitle, eyebrow, icon, actions, className = '', children, arrows = 'head' }) {
    const trackRef = useRef(null)
    const [edges, setEdges] = useState({ start: true, end: true })

    useEffect(() => {
        const track = trackRef.current
        if (!track) return undefined
        const update = () => {
            const max = track.scrollWidth - track.clientWidth
            setEdges({ start: track.scrollLeft <= 2, end: track.scrollLeft >= max - 2 })
        }
        update()
        track.addEventListener('scroll', update, { passive: true })
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
        observer?.observe(track)
        return () => {
            track.removeEventListener('scroll', update)
            observer?.disconnect()
        }
    }, [children])

    const scrollBy = (dir) => {
        const track = trackRef.current
        if (track) track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: 'smooth' })
    }
    const scrollable = !(edges.start && edges.end)

    if (arrows === 'sides') {
        return (
            <div className="rail rail--sides">
                {scrollable && (
                    <button type="button" className="rail__side is-prev" onClick={() => scrollBy(-1)} disabled={edges.start} aria-label="Previous products">
                        <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                    </button>
                )}
                <ul ref={trackRef} className="rail__track" aria-label={title}>
                    {children}
                </ul>
                {scrollable && (
                    <button type="button" className="rail__side is-next" onClick={() => scrollBy(1)} disabled={edges.end} aria-label="Next products">
                        <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                    </button>
                )}
            </div>
        )
    }

    return (
        <section className={`rail ${className}`.trim()} aria-labelledby={`rail-${id}`}>
            <div className="rail__head">
                <div className="rail__intro">
                    {icon && (
                        <span className="rail__icon" aria-hidden="true">
                            <i className={`fa-solid ${icon}`} />
                        </span>
                    )}
                    <div>
                        {eyebrow && <span className="rail__eyebrow">{eyebrow}</span>}
                        <h2 id={`rail-${id}`}>{title}</h2>
                        {subtitle && <p>{subtitle}</p>}
                    </div>
                </div>
                {(actions || scrollable) && (
                    <div className="rail__tools">
                        {actions}
                        {scrollable && (
                            <div className="rail__nav">
                                <button type="button" onClick={() => scrollBy(-1)} disabled={edges.start} aria-label={`Previous: ${title}`}>
                                    <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                                </button>
                                <button type="button" onClick={() => scrollBy(1)} disabled={edges.end} aria-label={`Next: ${title}`}>
                                    <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <ul ref={trackRef} className="rail__track">
                {children}
            </ul>
        </section>
    )
}

export default ProductRail
